// ForgotPassword — route /auth/forgot-password (public, unauthenticated).
// Story #6712 (UX-004). Self-service reset using Cognito ForgotPassword (emails
// a code) then ConfirmForgotPassword (code + new password). Same raw-fetch
// helpers and visual shell as the MFA recovery pages. Light theme only.
import { useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { Loader2, MailCheck, CheckCircle2 } from 'lucide-react'
import { forgotPassword, confirmForgotPassword, type CognitoAuthResponse } from '../../lib/cognito'
import { TALK_TO_US_EMAIL } from '../../constants/offer'

const MIN_PASSWORD_LENGTH = 12
function unmetPasswordRule(pw: string): string | null {
  if (pw.length < MIN_PASSWORD_LENGTH) return `Use at least ${MIN_PASSWORD_LENGTH} characters.`
  if (!/[A-Z]/.test(pw)) return 'Include at least one upper-case letter.'
  if (!/[a-z]/.test(pw)) return 'Include at least one lower-case letter.'
  if (!/[0-9]/.test(pw)) return 'Include at least one number.'
  if (!/[^A-Za-z0-9]/.test(pw)) return 'Include at least one symbol.'
  return null
}

function cognitoError(res: CognitoAuthResponse, fallback: string): string {
  switch (res.__type) {
    case 'CodeMismatchException': return 'That code is not right. Check the email and try again.'
    case 'ExpiredCodeException': return 'That code has expired. Go back and request a new one.'
    case 'InvalidPasswordException': return res.message || 'That password does not meet the rules.'
    case 'LimitExceededException':
    case 'TooManyRequestsException':
    case 'TooManyFailedAttemptsException': return 'Too many attempts. Wait a few minutes and try again.'
    default: return res.message || fallback
  }
}

export default function ForgotPassword() {
  const [step, setStep] = useState<'request' | 'confirm' | 'done'>('request')
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  async function requestCode(e: FormEvent) {
    e.preventDefault()
    setError('')
    if (!email.includes('@')) { setError('Enter the email address you sign in with.'); return }
    setBusy(true)
    try {
      const res = await forgotPassword(window.INNOVATEFLOW_CONFIG, email.trim())
      // Cognito answers UserNotFoundException only when user-existence hiding is off;
      // either way we show the same "check your email" step so the page never confirms
      // whether an address has an account.
      if (res.__type && res.__type !== 'UserNotFoundException' && res.__type !== 'InvalidParameterException') {
        setError(cognitoError(res, 'Could not send a reset code. Please try again.'))
        return
      }
      setStep('confirm')
    } catch {
      setError('Could not reach the sign-in service. Check your connection and try again.')
    } finally {
      setBusy(false)
    }
  }

  async function confirm(e: FormEvent) {
    e.preventDefault()
    setError('')
    if (!code.trim()) { setError('Enter the code from the email.'); return }
    const unmet = unmetPasswordRule(newPassword)
    if (unmet) { setError(unmet); return }
    setBusy(true)
    try {
      const res = await confirmForgotPassword(window.INNOVATEFLOW_CONFIG, email.trim(), code.trim(), newPassword)
      if (res.__type) {
        setError(cognitoError(res, 'Could not reset your password. Please try again.'))
        return
      }
      setStep('done')
    } catch {
      setError('Could not reach the sign-in service. Check your connection and try again.')
    } finally {
      setBusy(false)
    }
  }

  const inputClass =
    'w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder-slate-400 outline-none focus:border-iflow-500 focus:ring-2 focus:ring-iflow-200'

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-gradient-to-br from-sky-50 via-white to-[#E8EFF4]">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src="/logo.png" alt="InnovateFlow" className="w-14 h-14 rounded-xl mx-auto mb-3" />
          <h1 className="text-2xl font-bold text-iflow-900">Reset your password</h1>
        </div>

        <div className="bg-white rounded-2xl border border-[rgba(12,74,110,0.12)] p-8 shadow-xl">
          {step === 'done' ? (
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center mx-auto mb-3"><CheckCircle2 size={22} /></div>
              <h2 className="text-lg font-semibold text-iflow-900">Password updated</h2>
              <p className="text-sm text-slate-600 mt-2">You can now sign in with your new password.</p>
              <Link to="/login" className="inline-block mt-5 py-2.5 px-5 bg-iflow-600 text-white text-sm font-semibold rounded-lg hover:bg-iflow-700">Sign in</Link>
            </div>
          ) : (
            <form onSubmit={step === 'request' ? requestCode : confirm} className="space-y-4">
              {error && <div role="alert" className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>}

              {step === 'request' ? (
                <>
                  <p className="text-sm text-slate-600">Enter the email you sign in with and we will email you a code to set a new password.</p>
                  <div>
                    <label htmlFor="fp-email" className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                    <input id="fp-email" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)}
                      className={inputClass} placeholder="you@example.com" required />
                  </div>
                  <button type="submit" disabled={busy}
                    className="w-full inline-flex items-center justify-center gap-2 py-2.5 bg-iflow-600 text-white text-sm font-semibold rounded-lg hover:bg-iflow-700 disabled:opacity-60">
                    {busy && <Loader2 size={16} className="animate-spin" />} Email me a code
                  </button>
                </>
              ) : (
                <>
                  <div className="flex items-center gap-2 text-emerald-700"><MailCheck size={18} /><span className="text-sm font-semibold">Check your email</span></div>
                  <p className="text-sm text-slate-600">
                    If an account exists for {email}, we have sent a code. Enter it below with your new password. No email? Check spam,
                    or write to {TALK_TO_US_EMAIL}.
                  </p>
                  <div>
                    <label htmlFor="fp-code" className="block text-sm font-medium text-slate-700 mb-1">Code from the email</label>
                    <input id="fp-code" type="text" inputMode="numeric" autoComplete="one-time-code" value={code}
                      onChange={(e) => setCode(e.target.value)} className={`${inputClass} font-mono tracking-wider`} placeholder="123456" required />
                  </div>
                  <div>
                    <label htmlFor="fp-new" className="block text-sm font-medium text-slate-700 mb-1">New password</label>
                    <input id="fp-new" type="password" autoComplete="new-password" value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)} className={inputClass} required />
                    <p className="text-xs text-slate-600 mt-1">At least 12 characters with upper-case, lower-case, a number and a symbol.</p>
                  </div>
                  <button type="submit" disabled={busy}
                    className="w-full inline-flex items-center justify-center gap-2 py-2.5 bg-iflow-600 text-white text-sm font-semibold rounded-lg hover:bg-iflow-700 disabled:opacity-60">
                    {busy && <Loader2 size={16} className="animate-spin" />} Set new password
                  </button>
                  <button type="button" onClick={() => { setStep('request'); setError('') }}
                    className="w-full text-sm font-medium text-iflow-600 hover:text-iflow-700">Use a different email</button>
                </>
              )}
              <p className="text-center text-xs text-slate-600">
                <Link to="/login" className="text-iflow-600 font-medium hover:text-iflow-700">Back to sign in</Link>
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}
